import React, { useState } from 'react';
import {
	EllipsisHorizontalIcon,
	ExclamationTriangleIcon,
	NoSymbolIcon,
	ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';

import { capitalizeFirstLetter, classNames } from '../helpers/functions';

import EllipsisDropdown, { EllipsisDropdownItem } from '../EllipsisDropdown';

import { TemplateDropdown } from '../Attributes/Attributes_old';
import LoadingSkeleton from '../Shell/LoadingSkeleton';
import Button from '../Button';

export const BaseList = ({ items }) => {
	return items?.length ? (
		<ul role="list" className="divide-y divide-gray-100 text-xs -mt-1">
			{items?.map((item) => (
				<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-1.5 sm:flex-nowrap text-xs">
					{item.children}
				</li>
			))}
		</ul>
	) : null;
};

const WidgetBase = ({
	title,
	subtitle,
	menuItems,
	children,
	cols = 2,
	rows = 2,
	flexCenter,
	isLoading,
	isInactive,
	isError,
	isEmpty,
	isEmptyMessage,
	onIntegrationClick,
	onRefresh,
	titleListeners, // this is for drag and drop only
	allowResize = ['quarter', 'half', 'full'],
	onResize,
	options = [],
	optionsOnChange,
	optionSelected,
	fullHeight = false,
	skipIcon,
}) => {
	let DisplayComponent = null;
	const [size, setSize] = useState(
		cols === 1 ? 'quarter' : cols === 2 ? 'half' : 'full',
	);

	const handleResize = (size) => {
		if (typeof onResize === 'function') {
			onResize(size);
		}
		setSize(size);
	};

	if (isLoading) {
		DisplayComponent = (
			<div className="w-full">
				<LoadingSkeleton />
			</div>
		);
	} else if (isInactive) {
		DisplayComponent = (
			<>
				<ShieldExclamationIcon className="text-gray-400 w-10 h-10" />{' '}
				<div className="mt-3 text-gray-500 text-sm">
					Integration not configured
				</div>
				{typeof onIntegrationClick === 'function' && (
					<Button
						type="secondary"
						className="mt-4 text-xxs"
						onClick={() => onIntegrationClick()}
					>
						Configure
					</Button>
				)}
			</>
		);
	} else if (isEmpty) {
		DisplayComponent = (
			<>
				{!skipIcon && <NoSymbolIcon className="text-gray-400 w-10 h-10" />}
				<div className="mt-3 text-gray-500 text-sm">
					{isEmptyMessage || 'No data'}
				</div>
			</>
		);
	} else if (isError) {
		DisplayComponent = (
			<>
				<ExclamationTriangleIcon className="text-red-400 w-10 h-10" />{' '}
				<div className="mt-3 text-gray-500 text-sm">Error fetching data</div>
				{typeof onRefresh === 'function' && (
					<Button
						type="secondary"
						className="mt-4 text-xxs"
						onClick={() => onRefresh()}
					>
						Try again
					</Button>
				)}
			</>
		);
	} else {
		DisplayComponent = children;
	}

	let menuListItems = menuItems?.length ? [...menuItems] : [];

	if (allowResize && allowResize?.length) {
		allowResize?.map((size) =>
			menuListItems.push({
				name: `${capitalizeFirstLetter(size)} Size`,
				value: size,
				onClick: (size) => {
					handleResize(size?.value);
				},
			}),
		);
	}

	const sizeMapping = {
		quarter: 'col-span-1',
		half: 'col-span-2',
		full: 'col-span-4',
	};

	return (
		<div
			className={classNames(
				'sm:shadow shadow-slate-200 rounded-lg group flex flex-col sm:max-h-96',
				fullHeight ? 'sm:h-96' : '',
				sizeMapping[size || 'full'],
				`sm:h-[${rows === 1 ? '12' : '24'}rem]`,
			)}
		>
			{title || menuListItems?.length ? (
				<div
					className="px-0 sm:px-6 pt-0 sm:pt-6 flex items-center justify-between select-none	"
					{...titleListeners}
				>
					<div className="sm:flex items-center">
						{title && (
							<h2 className="flex sm:items-baseline flex-col sm:flex-row text-base/7 font-semibold text-zinc-950 sm:text-sm/6 mr-4">
								{title}
								<span className="text-xs text-gray-400 ml-3 font-normal">
									{subtitle}
								</span>
							</h2>
						)}

						{options?.length ? (
							<TemplateDropdown
								className="sm:ml-4 w-36"
								options={options}
								value={optionSelected}
								onChange={(index, value, raw) => {
									if (typeof optionsOnChange === 'function') {
										optionsOnChange(index, value, raw);
									}
								}}
								border={false}
							/>
						) : null}
					</div>
					<div className="flex items-center ">
						{menuListItems?.length ? (
							<EllipsisDropdown
								EllipsisButton={
									<Menu.Button className="p-2 border rounded-lg cursor-pointer opacity-0 group-hover:opacity-100 ease-in-out duration-200">
										<span className="sr-only">Open options</span>
										<EllipsisHorizontalIcon className="w-4 h-4 text-gray-500" />
									</Menu.Button>
								}
							>
								{menuListItems?.map((item, idx) => (
									<EllipsisDropdownItem
										onClick={() => item?.onClick(item)}
										className="text-xs"
										key={idx}
									>
										{item?.name}
									</EllipsisDropdownItem>
								))}
							</EllipsisDropdown>
						) : null}
					</div>
				</div>
			) : null}
			<div
				className={classNames(
					'p-0 sm:p-6 text-xs sm:flex flex-col flex-grow items-center flex-1 overflow-auto',
					flexCenter ? 'justify-center' : '',
				)}
			>
				{flexCenter ? (
					DisplayComponent
				) : (
					<div
						className={classNames(
							'w-full overflow-y-scroll',
							fullHeight ? 'h-96' : '',
						)}
					>
						{DisplayComponent}
					</div>
				)}
			</div>
		</div>
	);
};

export default WidgetBase;
